'use client';

import * as Sentry from '@sentry/nextjs';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

// Generate a unique anonymous user ID
function getAnonymousUserId() {
  if (typeof window === 'undefined') return null;

  const storageKey = 'sentry_anon_user_id';
  let userId = localStorage.getItem(storageKey);

  if (!userId) {
    // Generate a UUID-like ID
    userId = 'anon_' + crypto.randomUUID();
    localStorage.setItem(storageKey, userId);
  }

  return userId;
}

// Get session start time for tracking session duration
function getSessionInfo() {
  if (typeof window === 'undefined') return null;

  const sessionKey = 'sentry_session_start';
  let sessionStart = sessionStorage.getItem(sessionKey);

  if (!sessionStart) {
    sessionStart = Date.now().toString();
    sessionStorage.setItem(sessionKey, sessionStart);
  }

  return {
    startTime: parseInt(sessionStart, 10),
    duration: Date.now() - parseInt(sessionStart, 10),
  };
}

// Define funnel stages based on URL patterns
function getFunnelStage(pathname) {
  if (!pathname) return 'unknown';

  // Remove locale prefix (e.g., /en-us, /de, etc.)
  const pathWithoutLocale = pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?/i, '') || '/';

  const stages = {
    '/': 'landing',
    '/create-your-book': 'create_book',
    '/thank-you': 'purchase_complete',
    '/log-in': 'auth',
    '/my-orders': 'orders',
    '/reorder': 'reorder',
  };

  return stages[pathWithoutLocale] || 'other';
}

// Track custom events to Sentry
export function trackSentryEvent(eventName, data = {}) {
  const sessionInfo = getSessionInfo();

  Sentry.addBreadcrumb({
    category: 'user.action',
    message: eventName,
    level: 'info',
    data: {
      ...data,
      sessionDuration: sessionInfo?.duration,
      timestamp: new Date().toISOString(),
    },
  });

  // Also set as a tag for easier filtering
  Sentry.setTag('last_action', eventName);
}

// Track funnel step
export function trackFunnelStep(step, data = {}) {
  Sentry.addBreadcrumb({
    category: 'funnel',
    message: `Funnel: ${step}`,
    level: 'info',
    data: {
      step,
      ...data,
      timestamp: new Date().toISOString(),
    },
  });

  Sentry.setTag('funnel_step', step);
}

// Main tracker component
export default function SentryTracker() {
  const pathname = usePathname();
  const previousPathRef = useRef(null);
  const isInitializedRef = useRef(false);

  // Initialize user on mount
  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    const userId = getAnonymousUserId();
    const sessionInfo = getSessionInfo();

    if (userId) {
      // Set user identity in Sentry
      Sentry.setUser({
        id: userId,
        // You can add more user properties here when available:
        // email: userEmail,
        // username: userName,
      });

      // Set initial context
      Sentry.setContext('session', {
        startTime: new Date(sessionInfo?.startTime).toISOString(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        screenSize: typeof window !== 'undefined'
          ? `${window.innerWidth}x${window.innerHeight}`
          : 'unknown',
        referrer: typeof document !== 'undefined' ? document.referrer : 'unknown',
      });

      // Set initial tags
      Sentry.setTag('user_type', 'anonymous');
      Sentry.setTag('browser_session_start', sessionInfo?.startTime.toString());

      // Log initialization breadcrumb
      Sentry.addBreadcrumb({
        category: 'session',
        message: 'Session started',
        level: 'info',
        data: {
          userId,
          referrer: typeof document !== 'undefined' ? document.referrer : 'unknown',
        },
      });
    }
  }, []);

  // Track page views and funnel stages
  useEffect(() => {
    if (!pathname || pathname === previousPathRef.current) return;

    const previousPath = previousPathRef.current;
    previousPathRef.current = pathname;

    const funnelStage = getFunnelStage(pathname);
    const sessionInfo = getSessionInfo();

    // Add page view breadcrumb
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: `Navigated to ${pathname}`,
      level: 'info',
      data: {
        from: previousPath,
        to: pathname,
        funnelStage,
        sessionDuration: sessionInfo?.duration,
      },
    });

    // Update tags
    Sentry.setTag('current_page', pathname);
    Sentry.setTag('funnel_stage', funnelStage);

    // Track specific funnel events
    if (funnelStage === 'landing') {
      trackFunnelStep('viewed_landing', { source: previousPath || 'direct' });
    } else if (funnelStage === 'create_book') {
      trackFunnelStep('started_book_creation', { fromPage: previousPath });
    } else if (funnelStage === 'purchase_complete') {
      trackFunnelStep('completed_purchase', { fromPage: previousPath });
    }

  }, [pathname]);

  return null;
}

// Hook for manual tracking in components
export function useSentryTracking() {
  return {
    trackEvent: trackSentryEvent,
    trackFunnelStep,
    setUserProperty: (key, value) => {
      Sentry.setTag(`user_${key}`, value);
    },
    setContext: (name, data) => {
      Sentry.setContext(name, data);
    },
    identifyUser: (userInfo) => {
      Sentry.setUser({
        id: userInfo.id || getAnonymousUserId(),
        email: userInfo.email,
        username: userInfo.username,
        ...userInfo,
      });
      Sentry.setTag('user_type', userInfo.email ? 'registered' : 'anonymous');
    },
  };
}
